import { Drawer, Flex, FormRow, FormSet, FormStatus, H2, Input, Tag, ToggleButton } from '@dnb/eufemia';
import { filter as FilterIcon } from '@dnb/eufemia/icons';
import { ApiDto, ApiType } from '@portals/shared/portal/ApiDto';
import { useMemo, useState } from 'react';

import ApiCard from '@/pages/profile/apps/components/ApiCard';

const apiTypeToDisplayName: Record<ApiType, string> = {
  [ApiType.EVENT]: 'Event',
  [ApiType.REST]: 'API',
  [ApiType.MCP]: 'MCP',
};

function apiSearchQueryFilter(api: ApiDto, searchQuery: string): boolean {
  const searchQueryLowerCase = searchQuery.toLowerCase();

  if (api.name.toLowerCase().includes(searchQueryLowerCase)) {
    return true;
  }
  if (api.slug.toLowerCase().includes(searchQueryLowerCase)) {
    return true;
  }
  if (api.description?.toLowerCase().includes(searchQueryLowerCase)) {
    return true;
  }
  return api.scopes?.some((scope) => scope.name.toLowerCase().includes(searchQueryLowerCase)) ?? false;
}

interface Props {
  apis: ApiDto[];
  isDnbUser: boolean;
  onSelect: (api?: ApiDto) => void;
  selectedApiIds?: string[];
}

export default function SelectIntegrationServices({ apis, isDnbUser, onSelect, selectedApiIds }: Props) {
  const [searchQuery, setSearchQuery] = useState<string>();
  const [apiTypeFilter, setApiTypeFilter] = useState<ApiType[]>([]);

  const filteredSearchQueryApis = useMemo(() => {
    if (!searchQuery) return apis;
    return apis.filter((api) => apiSearchQueryFilter(api, searchQuery));
  }, [apis, searchQuery]);

  // This must be calculated before filteredApiTypeApis, otherwise unselected types will become 0
  const apiTypeCount: Record<ApiType, number> = useMemo(() => {
    return filteredSearchQueryApis.reduce<Record<string, number>>((result, api) => {
      result[api.type] = result[api.type] ? ++result[api.type]! : 1;
      return result;
    }, {});
  }, [filteredSearchQueryApis]);

  const filteredApiTypeApis = useMemo(() => {
    if (apiTypeFilter.length === 0) {
      return filteredSearchQueryApis;
    }
    return filteredSearchQueryApis.filter((api) => apiTypeFilter.includes(api.type));
  }, [filteredSearchQueryApis, apiTypeFilter]);

  return (
    <FormRow direction="vertical">
      <H2 bottom="small" top={false}>
        Select integration service
      </H2>
      {isDnbUser && (
        <Flex.Vertical bottom="small" gap="small">
          <Drawer
            title="Filters"
            triggerAttributes={{
              text: 'Filter',
              variant: 'secondary',
              icon: FilterIcon,
            }}
          >
            <FormSet vertical>
              <FormRow top="medium">
                <ToggleButton.Group
                  label="Type"
                  multiselect
                  on_change={({ values }) => setApiTypeFilter([...values])}
                  values={apiTypeFilter}
                  variant="checkbox"
                >
                  {Object.values(ApiType).map((apiType) => (
                    <ToggleButton key={apiType} value={apiType}>
                      {`${apiTypeToDisplayName[apiType]} (${apiTypeCount[apiType] ?? 0})`}
                    </ToggleButton>
                  ))}
                </ToggleButton.Group>
              </FormRow>
            </FormSet>
          </Drawer>
          {apiTypeFilter.length > 0 && (
            <Tag.Group label="Filters">
              {apiTypeFilter.map((apiType) => (
                <Tag
                  key={apiType}
                  onClick={() => setApiTypeFilter(apiTypeFilter.filter((candidate) => candidate !== apiType))}
                  variant="removable"
                >
                  {apiTypeToDisplayName[apiType]}
                </Tag>
              ))}
            </Tag.Group>
          )}
        </Flex.Vertical>
      )}
      <Input
        bottom="medium"
        on_change={({ value }) => setSearchQuery(value)}
        placeholder="Search by name, description, scopes..."
        stretch
        type="search"
      />
      {apis.length === 0 ? (
        <FormStatus
          state="warn"
          text={
            isDnbUser
              ? 'No integration services available to attach.'
              : 'We have no integration services available for onboarding at the moment. More integration services like the Currencies api will be made available for onboarding later.'
          }
        />
      ) : (
        <Flex.Stack>
          {filteredApiTypeApis.map((api) => (
            <ApiCard
              api={api}
              key={api.id}
              onSelect={(api) => onSelect(api)}
              selected={selectedApiIds?.includes(api.id)}
            />
          ))}
        </Flex.Stack>
      )}
    </FormRow>
  );
}
